import { useRelativeTime } from '../hooks/useRelativeTime';
import { isAmber, scheduleStatePhrase } from '../lib/scheduleAliveness';
import type { ScheduleState } from '../lib/scheduleAliveness';

// ─────────────────────────────────────────────────────────────────────────────
// ThreadScheduleAffix — the amber CONFESSION in the space header (T-0157 → T-0200).
//
// Since T-0200 this affix is amber-ONLY. The armed/rest rhythm it used to carry
// ("repeats daily · next 8:00") moved to the SpaceStateChip beside the title,
// which speaks the rest register ("asleep · wakes 16:40"). What remains here is
// the past tense: a fire that ran late, never landed, or was never answered.
//
// This is still a ● surface. Tizok owns the timer, so when a fire proves it did
// not keep the promise, the affix confesses in plain past tense — never a modal.
// The confession is AGED ("… fired 2h ago …") so it can't read as pinned-forever,
// and it CLEARS the instant a machine takes the turn (`fireConfession` stops
// returning an amber state once `turn_taken`).
//
// Driven by props (the header resolves the ● schedule once via
// `useSpaceScheduleState` and shares it with the chip) so both surfaces read the
// exact same fire.
// ─────────────────────────────────────────────────────────────────────────────

export function ThreadScheduleAffix({
  scheduleState,
  /** When the latest fire actually fired — the anchor for the "2h ago" age. Only
   *  the awaitingMachine confession uses it; the others carry their own times. */
  firedAt,
}: {
  scheduleState: ScheduleState | null;
  firedAt?: string | null;
}) {
  // Hook must run unconditionally; the relative string is only woven in below,
  // and only for the awaitingMachine state, where `firedAt` is a real instant.
  const firedAgo = useRelativeTime(firedAt ?? '');

  if (!scheduleState || !isAmber(scheduleState)) return null;

  const aged =
    scheduleState.kind === 'awaitingMachine' && firedAt ? { firedAgo } : undefined;

  return (
    <span className="space-header-schedule" data-tone="amber">
      {scheduleStatePhrase(scheduleState, aged)}
    </span>
  );
}
